import { CalendarDate } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";

type RouterOutput = inferRouterOutputs<AppRouter>;
type ReportResult = RouterOutput["reportsV3"]["referralSourceReport"];
type ContactRow = ReportResult["contacts"][number];
type Insight = ReportResult["insights"][number];

type SortField =
    | "contactName"
    | "leadCount"
    | "movedCount"
    | "moveRate"
    | "avgDaysToMove"
    | "totalInvoicedCents"
    | "avgInvoicePerMoveCents";

type ViewMode = "flat" | "category";

interface DatePreset {
    label: string;
    getRange: () => { start: CalendarDate; end: CalendarDate };
}

const getDatePresets = (): DatePreset[] => {
    const today = CalendarDate.today;
    return [
        {
            label: "Year to Date",
            getRange: () => ({
                start: new CalendarDate(today.year, 1, 1),
                end: today,
            }),
        },
        {
            label: "This Month",
            getRange: () => ({
                start: new CalendarDate(today.year, today.month, 1),
                end: today,
            }),
        },
        {
            label: "Last Month",
            getRange: () => {
                const lastMonth =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 12, 1)
                        : new CalendarDate(today.year, today.month - 1, 1);
                const endDay = new Date(lastMonth.year, lastMonth.month, 0).getDate();
                return {
                    start: lastMonth,
                    end: new CalendarDate(lastMonth.year, lastMonth.month, endDay),
                };
            },
        },
        {
            label: "This Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                return {
                    start: new CalendarDate(today.year, q * 3 + 1, 1),
                    end: today,
                };
            },
        },
        {
            label: "Last Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                const prevQ = q === 0 ? 3 : q - 1;
                const year = q === 0 ? today.year - 1 : today.year;
                const startMonth = prevQ * 3 + 1;
                const endMonth = startMonth + 2;
                const endDay = new Date(year, endMonth, 0).getDate();
                return {
                    start: new CalendarDate(year, startMonth, 1),
                    end: new CalendarDate(year, endMonth, endDay),
                };
            },
        },
        {
            label: "Last 12 Months",
            getRange: () => {
                const start =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 1, today.day)
                        : new CalendarDate(today.year - 1, today.month, today.day);
                return { start, end: today };
            },
        },
        {
            label: "Last Year",
            getRange: () => ({
                start: new CalendarDate(today.year - 1, 1, 1),
                end: new CalendarDate(today.year - 1, 12, 31),
            }),
        },
    ];
};

const PAGE_SIZE = 25;

const COLORS = {
    leads: "#2a6496",
    moved: "#2d7a4f",
    invoiced: "#7a3c9e",
    avgMove: "#b85c2a",
};

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

const formatPercent = (rate: number | null): string => {
    if (rate === null) return "-";
    return `${Math.round(rate * 100)}%`;
};

const ProgressBar: React.FC<{
    value: number;
    max: number;
    color: string;
}> = ({ value, max, color }) => {
    const width = max > 0 ? (value / max) * 100 : 0;
    return (
        <div
            className="w-full rounded-full mt-1"
            style={{ height: 4, backgroundColor: "#e5e7eb" }}>
            <div
                className="rounded-full"
                style={{
                    height: 4,
                    width: `${Math.min(width, 100)}%`,
                    backgroundColor: color,
                }}
            />
        </div>
    );
};

const MoveRatePill: React.FC<{ rate: number | null }> = ({ rate }) => {
    if (rate === null) return <span className="text-gray-400">-</span>;
    const pct = Math.round(rate * 100);
    let bgColor = "#f97316";
    let textColor = "#9a3412";
    if (pct >= 70) {
        bgColor = "#22c55e";
        textColor = "#166534";
    } else if (pct >= 50) {
        bgColor = "#eab308";
        textColor = "#854d0e";
    }
    return (
        <span
            className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: bgColor + "22", color: textColor }}>
            {pct}%
        </span>
    );
};

export const ReferralSource: React.FC = () => {
    const regionId = useRegionId();

    const presets = useMemo(() => getDatePresets(), []);
    const defaultRange = presets[0].getRange();

    const [startDate, setStartDate] = useState(defaultRange.start.toString());
    const [endDate, setEndDate] = useState(defaultRange.end.toString());
    const [sortField, setSortField] = useState<SortField>("leadCount");
    const [sortAsc, setSortAsc] = useState(false);
    const [page, setPage] = useState(0);
    const [viewMode, setViewMode] = useState<ViewMode>("flat");
    const [categoryFilter, setCategoryFilter] = useState<string>("all");
    const [assignedUserFilter, setAssignedUserFilter] = useState<string>("all");

    const trpcClient = trpc.useTRPC();

    const reportQuery = useQuery(
        trpcClient.reportsV3.referralSourceReport.queryOptions(
            {
                startDate,
                endDate,
                regionId: regionId || "",
            },
            { enabled: !!regionId },
        ),
    );

    const filteredContacts = useMemo(() => {
        if (!reportQuery.data) return [];
        let contacts = reportQuery.data.contacts;
        if (categoryFilter !== "all") {
            if (categoryFilter === "uncategorized") {
                contacts = contacts.filter(c => !c.organizationType);
            } else {
                contacts = contacts.filter(c => c.organizationType === categoryFilter);
            }
        }
        if (assignedUserFilter !== "all") {
            contacts = contacts.filter(c => c.assignedUserId === assignedUserFilter);
        }
        return contacts;
    }, [reportQuery.data, categoryFilter, assignedUserFilter]);

    const sortedContacts = useMemo(() => {
        const sorted = [...filteredContacts].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (aVal === null && bVal === null) return 0;
            if (aVal === null) return 1;
            if (bVal === null) return -1;
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortAsc
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
        return sorted;
    }, [filteredContacts, sortField, sortAsc]);

    const paginatedContacts = useMemo(() => {
        const start = 0;
        const end = (page + 1) * PAGE_SIZE;
        return sortedContacts.slice(start, end);
    }, [sortedContacts, page]);

    const maxValues = useMemo(() => {
        if (filteredContacts.length === 0)
            return {
                leads: 1,
                moved: 1,
                invoiced: 1,
                avgMove: 1,
            };
        return {
            leads: Math.max(...filteredContacts.map(c => c.leadCount), 1),
            moved: Math.max(...filteredContacts.map(c => c.movedCount), 1),
            invoiced: Math.max(...filteredContacts.map(c => c.totalInvoicedCents), 1),
            avgMove: Math.max(
                ...filteredContacts.map(c => c.avgInvoicePerMoveCents || 0),
                1,
            ),
        };
    }, [filteredContacts]);

    const summary = useMemo(() => {
        const totalLeads = filteredContacts.reduce((s, c) => s + c.leadCount, 0);
        const totalMoved = filteredContacts.reduce((s, c) => s + c.movedCount, 0);
        const totalInvoiced = filteredContacts.reduce(
            (s, c) => s + c.totalInvoicedCents,
            0,
        );
        const moveRate = totalLeads > 0 ? totalMoved / totalLeads : null;

        const movedWithDays = filteredContacts.filter(
            c => c.avgDaysToMove !== null && c.movedCount > 0,
        );
        let avgDays: number | null = null;
        if (movedWithDays.length > 0) {
            const totalDays = movedWithDays.reduce(
                (s, c) => s + (c.avgDaysToMove || 0) * c.movedCount,
                0,
            );
            const totalMovedWithDays = movedWithDays.reduce(
                (s, c) => s + c.movedCount,
                0,
            );
            avgDays =
                totalMovedWithDays > 0
                    ? Math.round(totalDays / totalMovedWithDays)
                    : null;
        }

        return {
            totalContacts: filteredContacts.length,
            totalLeads,
            totalMoved,
            moveRate,
            avgDays,
            totalInvoiced,
        };
    }, [filteredContacts]);

    const categories = useMemo(() => {
        if (!reportQuery.data) return [];
        const types = new Set<string>();
        let hasUncategorized = false;
        for (const c of reportQuery.data.contacts) {
            if (c.organizationType) {
                types.add(c.organizationType);
            } else {
                hasUncategorized = true;
            }
        }
        const sorted = Array.from(types).sort();
        if (hasUncategorized) sorted.push("uncategorized");
        return sorted;
    }, [reportQuery.data]);

    const assignedUsers = useMemo(() => {
        if (!reportQuery.data) return [];
        const users = new Map<string, string>();
        for (const c of reportQuery.data.contacts) {
            if (c.assignedUserId && c.assignedUserName) {
                users.set(c.assignedUserId, c.assignedUserName);
            }
        }
        return Array.from(users.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [reportQuery.data]);

    const groupedByCategory = useMemo(() => {
        if (viewMode !== "category") return null;
        const groups = new Map<string, ContactRow[]>();
        for (const c of sortedContacts) {
            const key = c.organizationType || "Uncategorized";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(c);
        }
        const entries = Array.from(groups.entries()).sort((a, b) => {
            if (a[0] === "Uncategorized") return 1;
            if (b[0] === "Uncategorized") return -1;
            return a[0].localeCompare(b[0]);
        });
        return entries;
    }, [sortedContacts, viewMode]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(false);
        }
        setPage(0);
    };

    const handlePreset = (preset: DatePreset) => {
        const range = preset.getRange();
        setStartDate(range.start.toString());
        setEndDate(range.end.toString());
        setPage(0);
    };

    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortAsc ? " \u25B2" : " \u25BC";
    };

    const renderTableRow = (contact: ContactRow) => (
        <tr key={contact.contactId} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4">
                <Link
                    to={`/references/contacts/show/${contact.contactId}`}
                    className="font-medium text-blue-700 hover:underline">
                    {contact.preferredName || contact.contactName}
                </Link>
                {contact.companyName && (
                    <div className="text-xs text-gray-500">{contact.companyName}</div>
                )}
                {contact.organizationType && (
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                        {contact.organizationType}
                    </span>
                )}
            </td>
            <td className="py-3 px-4 text-sm text-gray-700">
                {contact.assignedUserName || <span className="text-gray-400">-</span>}
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.leadCount}</div>
                <ProgressBar
                    value={contact.leadCount}
                    max={maxValues.leads}
                    color={COLORS.leads}
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.movedCount}</div>
                <ProgressBar
                    value={contact.movedCount}
                    max={maxValues.moved}
                    color={COLORS.moved}
                />
            </td>
            <td className="py-3 px-4 text-center">
                <MoveRatePill rate={contact.moveRate} />
            </td>
            <td className="py-3 px-4 text-right text-sm">
                {contact.avgDaysToMove !== null ? (
                    <span>
                        {contact.avgDaysToMove}
                        {contact.hasMissingMoveDate && (
                            <span
                                className="text-amber-500 ml-1"
                                title="Some moved clients are missing move dates">
                                *
                            </span>
                        )}
                    </span>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {contact.totalInvoicedCents > 0
                        ? formatCurrency(contact.totalInvoicedCents)
                        : "-"}
                </div>
                <ProgressBar
                    value={contact.totalInvoicedCents}
                    max={maxValues.invoiced}
                    color={COLORS.invoiced}
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {contact.avgInvoicePerMoveCents !== null
                        ? formatCurrency(contact.avgInvoicePerMoveCents)
                        : "-"}
                </div>
                {contact.avgInvoicePerMoveCents !== null && (
                    <ProgressBar
                        value={contact.avgInvoicePerMoveCents}
                        max={maxValues.avgMove}
                        color={COLORS.avgMove}
                    />
                )}
            </td>
        </tr>
    );

    const renderFooterTotals = () => {
        const totalLeads = filteredContacts.reduce((s, c) => s + c.leadCount, 0);
        const totalMoved = filteredContacts.reduce((s, c) => s + c.movedCount, 0);
        const totalInvoiced = filteredContacts.reduce(
            (s, c) => s + c.totalInvoicedCents,
            0,
        );
        const overallRate = totalLeads > 0 ? totalMoved / totalLeads : null;

        return (
            <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                <td className="py-3 px-4">Total ({filteredContacts.length} contacts)</td>
                <td className="py-3 px-4" />
                <td className="py-3 px-4 text-right">{totalLeads}</td>
                <td className="py-3 px-4 text-right">{totalMoved}</td>
                <td className="py-3 px-4 text-center">
                    <MoveRatePill rate={overallRate} />
                </td>
                <td className="py-3 px-4 text-right">
                    {summary.avgDays !== null ? summary.avgDays : "-"}
                </td>
                <td className="py-3 px-4 text-right">
                    {totalInvoiced > 0 ? formatCurrency(totalInvoiced) : "-"}
                </td>
                <td className="py-3 px-4" />
            </tr>
        );
    };

    const insights = useMemo(() => {
        if (!reportQuery.data) return [];
        return reportQuery.data.insights;
    }, [reportQuery.data]);

    return (
        <div className="p-6 max-w-full">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                    Referral Source Report
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    Performance metrics for each referral contact
                </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => {
                                setStartDate(e.target.value);
                                setPage(0);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => {
                                setEndDate(e.target.value);
                                setPage(0);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex flex-wrap gap-1 ml-2">
                        {presets.map(p => (
                            <button
                                key={p.label}
                                onClick={() => handlePreset(p)}
                                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100">
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {reportQuery.isLoading && (
                <div className="text-center py-12 text-gray-500">Loading report...</div>
            )}

            {reportQuery.isError && (
                <div className="text-center py-12 text-red-600">
                    Failed to load report. Please try again.
                </div>
            )}

            {reportQuery.data && (
                <>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <div>
                            <select
                                value={categoryFilter}
                                onChange={e => {
                                    setCategoryFilter(e.target.value);
                                    setPage(0);
                                }}
                                className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500">
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>
                                        {cat === "uncategorized" ? "Uncategorized" : cat}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <select
                                value={assignedUserFilter}
                                onChange={e => {
                                    setAssignedUserFilter(e.target.value);
                                    setPage(0);
                                }}
                                className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500">
                                <option value="all">All Users</option>
                                {assignedUsers.map(([id, name]) => (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex rounded-md border border-gray-300 overflow-hidden">
                            <button
                                onClick={() => setViewMode("flat")}
                                className={`px-3 py-1 text-xs font-medium ${
                                    viewMode === "flat"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}>
                                Flat
                            </button>
                            <button
                                onClick={() => setViewMode("category")}
                                className={`px-3 py-1 text-xs font-medium border-l border-gray-300 ${
                                    viewMode === "category"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}>
                                By Category
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500 uppercase font-medium">
                                Contacts
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {summary.totalContacts}
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div
                                className="text-xs uppercase font-medium"
                                style={{ color: COLORS.leads }}>
                                Total Leads
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {summary.totalLeads}
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div
                                className="text-xs uppercase font-medium"
                                style={{ color: COLORS.moved }}>
                                Total Moved
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {summary.totalMoved}
                            </div>
                            <div className="text-xs text-gray-500">
                                {formatPercent(summary.moveRate)} move rate
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="text-xs text-gray-500 uppercase font-medium">
                                Avg Days to Move
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {summary.avgDays !== null ? summary.avgDays : "-"}
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                            <div
                                className="text-xs uppercase font-medium"
                                style={{ color: COLORS.invoiced }}>
                                Total Invoiced
                            </div>
                            <div className="text-2xl font-bold mt-1">
                                {formatCurrency(summary.totalInvoiced)}
                            </div>
                        </div>
                    </div>

                    <div className="text-sm text-gray-500 mb-2">
                        Showing{" "}
                        {Math.min(paginatedContacts.length, sortedContacts.length)} of{" "}
                        {sortedContacts.length} contacts
                        {filteredContacts.length !==
                            (reportQuery.data?.contacts.length ?? 0) &&
                            ` (filtered from ${reportQuery.data?.contacts.length})`}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th
                                        className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort("contactName")}>
                                        Contact
                                        {sortIndicator("contactName")}
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">
                                        Assigned To
                                    </th>
                                    <th
                                        className="py-3 px-4 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-gray-100"
                                        style={{ color: COLORS.leads }}
                                        onClick={() => handleSort("leadCount")}>
                                        Leads
                                        {sortIndicator("leadCount")}
                                    </th>
                                    <th
                                        className="py-3 px-4 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-gray-100"
                                        style={{ color: COLORS.moved }}
                                        onClick={() => handleSort("movedCount")}>
                                        Moved
                                        {sortIndicator("movedCount")}
                                    </th>
                                    <th
                                        className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort("moveRate")}>
                                        Move Rate
                                        {sortIndicator("moveRate")}
                                    </th>
                                    <th
                                        className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort("avgDaysToMove")}>
                                        Avg Days
                                        {sortIndicator("avgDaysToMove")}
                                    </th>
                                    <th
                                        className="py-3 px-4 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-gray-100"
                                        style={{ color: COLORS.invoiced }}
                                        onClick={() => handleSort("totalInvoicedCents")}>
                                        Total Invoiced
                                        {sortIndicator("totalInvoicedCents")}
                                    </th>
                                    <th
                                        className="py-3 px-4 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-gray-100"
                                        style={{ color: COLORS.avgMove }}
                                        onClick={() =>
                                            handleSort("avgInvoicePerMoveCents")
                                        }>
                                        Avg/Move
                                        {sortIndicator("avgInvoicePerMoveCents")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewMode === "flat" ? (
                                    paginatedContacts.map(renderTableRow)
                                ) : (
                                    <>
                                        {groupedByCategory?.map(
                                            ([category, contacts]) => {
                                                const groupLeads = contacts.reduce(
                                                    (s, c) => s + c.leadCount,
                                                    0,
                                                );
                                                const groupMoved = contacts.reduce(
                                                    (s, c) => s + c.movedCount,
                                                    0,
                                                );
                                                const groupInvoiced = contacts.reduce(
                                                    (s, c) => s + c.totalInvoicedCents,
                                                    0,
                                                );
                                                const groupRate =
                                                    groupLeads > 0
                                                        ? Math.round(
                                                              (groupMoved / groupLeads) *
                                                                  100,
                                                          )
                                                        : 0;
                                                return (
                                                    <React.Fragment key={category}>
                                                        <tr className="bg-gray-100 border-b border-gray-200">
                                                            <td
                                                                colSpan={8}
                                                                className="py-2 px-4 text-sm font-semibold text-gray-700">
                                                                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2" />
                                                                {category}{" "}
                                                                <span className="font-normal text-gray-500">
                                                                    {contacts.length}{" "}
                                                                    contacts -{" "}
                                                                    {groupLeads} leads -{" "}
                                                                    {groupMoved} moved (
                                                                    {groupRate}
                                                                    %) -{" "}
                                                                    {formatCurrency(
                                                                        groupInvoiced,
                                                                    )}{" "}
                                                                    invoiced
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        {contacts.map(renderTableRow)}
                                                    </React.Fragment>
                                                );
                                            },
                                        )}
                                    </>
                                )}
                                {filteredContacts.length > 0 && renderFooterTotals()}
                            </tbody>
                        </table>
                    </div>

                    {sortedContacts.length > (page + 1) * PAGE_SIZE && (
                        <div className="text-center mt-4">
                            <button
                                onClick={() => setPage(page + 1)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                                Show More
                            </button>
                        </div>
                    )}

                    {insights.length > 0 && (
                        <div
                            className="mt-6 rounded-lg p-5"
                            style={{
                                background: "linear-gradient(135deg, #1a1a18, #2d2d28)",
                            }}>
                            <h3 className="text-white text-sm font-semibold uppercase tracking-wide mb-3">
                                Insights
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {insights.map((insight: Insight, i: number) => (
                                    <div key={i} className="text-gray-300 text-sm">
                                        {insight.text}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-4 text-xs text-gray-400 space-y-1">
                        <p>
                            * Move rate = moved clients / total leads. Moved statuses
                            include: Moved, Invoice Sent, Closed - Paid.
                        </p>
                        <p>
                            * Avg Days to Move is calculated from client created date to
                            move date. Clients without a move date are excluded.
                        </p>
                        <p>
                            * Only contacts with at least one lead in the selected date
                            range are shown.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};
